import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { PhoneInputComponent } from "../../../../@shared/components/phone-input/phone-input.component";
import { SelectModule } from 'primeng/select';
import { AuthenticationService } from '../../../../@core/services/authentication.service';
import { PasswordModule } from 'primeng/password';
import { ActivatedRoute, Router } from '@angular/router';
import slug from 'slug';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService } from 'primeng/api';
import { ToastService } from '../../../../@core/services/toast.service';
import { catchError } from "rxjs";
import { IUser } from '../../../../@core/models/model';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ButtonComponent } from '../../../../@core/components/ui/button/button.component';
import { SelectComponent, SelectOption, SelectConfig } from '../../../../@core/components/ui/select';
import { StepperModule } from 'primeng/stepper';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressBarModule } from 'primeng/progressbar';
import { validateEmail } from '../../../../common/utils';
import { LOGIN_METHOD } from '../../../../contract-management/models/contract-management.model';
import { ToggleComponent } from '../../../../@core/components/ui/toggle/toggle.component';
import { TextCasePipe } from "../../../../@shared/pipes/textCase.pipe";
import { CaseType } from '../../../../@shared/services/case-format.service';
import { InputComponent } from '../../../../@core/components/ui/input/input.component';
import { DEFAULT_PROFILE_PICTURES } from 'src/app/auth/sign-up/constants/constants';
import { AssetUrlPipe } from 'src/app/@shared/pipes/asset-url.pipe';

@Component({
  selector: 'app-onboard-org',
  standalone: true,
  imports: [FormsModule, CommonModule, ReactiveFormsModule, ButtonComponent, CheckboxModule, RadioButtonModule, CommonModule, SelectModule, PasswordModule, ConfirmPopupModule, ConfirmDialogModule, SelectComponent, StepperModule, PhoneInputComponent, TooltipModule, ButtonComponent, ProgressBarModule, ToggleComponent, TextCasePipe, InputComponent, AssetUrlPipe],
  templateUrl: './onboard-org.component.html',
  styleUrl: './onboard-org.component.scss',
  providers: [ConfirmationService]
})
export class OnboardOrgComponent implements OnInit {
  profileForm!: FormGroup;
  organizationForm!: FormGroup;
  currentStep = 1;
  totalSteps = 2;
  role: string = ''
  email: string = ''
  sendOverEmail = false
  currentUser!: IUser | null | undefined
  value!: string;
  passwordMatched: boolean = true;
  workspacePrefix = 'inncircles.ai/';
  workspaceInputValue = '';
  name: string = ''
  roleError: boolean = false;
  expertiseError: boolean = false;
  selectedGender: string = '';
  submitInProcess: boolean = false
  additionalInformation: boolean = false
  teamInviteStep: boolean = false

  domain: string = ''

  @ViewChild('additionalInfoSection') additionalInfoSection!: ElementRef;
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  PROFILE_PICTURES = computed(() => {
    return DEFAULT_PROFILE_PICTURES
  })

  constructor(
    private fb: FormBuilder,
    private authService: AuthenticationService,
    private route: ActivatedRoute,
    private router: Router,
    private toastService: ToastService,
    private confirmationService: ConfirmationService
  ) {
    this.authService.currentUser.subscribe(res => {
      this.currentUser = res
    });
    this.domain = this.route.snapshot.queryParams['domain'] ?? '';
  }

  CASE_TYPE = CaseType;

  ngOnInit(): void {
    this.email = this.route.snapshot.paramMap.get('email') ?? '';
    if (!this.email) {
      this.router.navigate(['/signup']);
      return
    }
    this.initializeForms();
  }

  roles = ["Project Manager", "Site Engineer", "Quantity Surveyor", "Contract Administrator", "Safety Officer", "Document Controller", "Estimator", "Bid Manager"];
  domainExpertise = ["Civil Works", "Structural Works", "Project Management", "Construction Law", "Quantity Surveying", "Environmental Engineering", "Risk Management", "Construction Safety"];
  members: { email: string, role: string, touched?: { email: boolean, role: boolean }, emailExists?: boolean }[] = []
  noOfEmployees: SelectOption[] = [
    { label: '1-10', value: '1-10' },
    { label: '11-50', value: '11-50' },
    { label: '51-100', value: '51-100' },
    { label: '101-500', value: '101-500' },
    { label: '501-1000', value: '501-1000' },
    { label: '1000+', value: '1000+' }
  ];
  userRoles: SelectOption[] = [
    { label: 'Admin', value: 'Admin' },
    { label: 'Member', value: 'Member' }
  ];
  imageFile: any
  selectedNoOfEmployees: string = ''
  validImageTypes: string[] = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
  maxImageSize: number = 5 * 1000 * 1000;

  loginMethods = [
    { label: 'Standard login with password', value: LOGIN_METHOD.PASSWORD, enabled: true, icon: null },
    { label: 'Google', value: LOGIN_METHOD.GOOGLE, enabled: false, icon: 'icons/google-icon.svg' },
    { label: 'Microsoft', value: LOGIN_METHOD.MICROSOFT, enabled: false, icon: 'icons/microsoft.svg' }
  ];

  companySizeConfig: SelectConfig = {
    placeholder: 'Company Size',
    searchable: false,
    clearable: false,
    styleClass: 'border h-10 rounded-lg w-full',
    style: { 'width': '100%' }
  };

  userRoleConfig: SelectConfig = {
    placeholder: 'Select a Role',
    searchable: false,
    clearable: false,
    styleClass: 'border w-full',
    style: { 'width': '100%' },
    appendTo: 'body'
  };

  // Calculates the percentage based on the current step
  get progress(): string {
    return `${(this.currentStep / this.totalSteps) * 100}%`;
  }

  getStepText(): string {
    return `Step ${this.currentStep} of ${this.totalSteps}`;
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler($event: Event) {
    $event.returnValue = true
  }

  // Move to the next step
  nextStep() {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
    }
  }

  // Move to the previous step
  previousStep() {
    if (this.submitInProcess) return;
    if (this.currentStep > 1) {
      this.currentStep--;
      this.additionalInformation = false;
      this.teamInviteStep = false;
    }
  }

  clearProfile() {
    if (this.submitInProcess) return;
    this.initializeForms();
    this.currentStep = 1;
    this.additionalInformation = false;
    this.teamInviteStep = false;
  }

  toggleAdditionalInformation() {
    if (this.submitInProcess) return;
    if (this.isCompanyInfoValid) {
      this.additionalInformation = !this.additionalInformation;

      if (this.additionalInformation) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.additionalInfoSection?.nativeElement?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest'
            });
          });
        });
      } else {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.scrollContainer?.nativeElement?.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
          });
        });
      }
    }
  }

  get isCompanyInfoValid(): boolean {
    const companyName = this.organizationForm.get('companyName')?.value;
    const companyEmail = this.organizationForm.get('companyEmail')?.value;
    const companyDomain = this.organizationForm.get('domain')?.value;
    const companyEmailValid = this.organizationForm.get('companyEmail')?.valid;
    const companyDomainValid = this.organizationForm.get('domain')?.valid;
    return !!(companyName && companyName.trim() && companyEmail && companyEmailValid && companyDomain && companyDomainValid);
  }

  get isOrganizationFormValid(): boolean {
    const companyNameControl = this.organizationForm.get('companyName');

    const companyEmailControl = this.organizationForm.get('companyEmail');

    const companyDomainControl = this.organizationForm.get('domain');

    const contactControl = this.organizationForm.get('contact');

    const companyNameValid = companyNameControl?.valid && companyNameControl?.value?.trim();
    const companyEmailValid = companyEmailControl?.valid && companyEmailControl?.value?.trim();
    const companyDomainValid = companyDomainControl?.valid && companyDomainControl?.value?.trim();
    const contactValid = !contactControl?.value || contactControl?.valid;
    return (companyNameValid && companyEmailValid && companyDomainValid && contactValid);
  }


  onUrlInput(event: any) {
    const inputValue = event.target.value;

    //TODO:  check for already existing workspace  
    this.authService.checkOrganization(inputValue.split('/')[1]).subscribe(res => {
      if (res.success) {
        this.organizationForm.controls['workspaceUrl'].setErrors({ 'alreadyExists': true });
      }
    })
    if (inputValue.startsWith(this.workspacePrefix)) {
      this.workspaceInputValue = inputValue.replace(this.workspacePrefix, '');
      this.workspaceInputValue = this.workspaceInputValue.replace(/[^a-zA-Z0-9-]/g, '-');
      this.workspaceInputValue = slug(this.workspaceInputValue, '-').toLocaleLowerCase();
    } else {
      event.target.value = this.workspacePrefix + this.workspaceInputValue;
      this.organizationForm.get('workspaceUrl')?.setValue(this.workspacePrefix + this.workspaceInputValue);
    }
  }

  addMember() {
    if (this.submitInProcess) return;
    this.members.push({ email: '', role: '', touched: { email: false, role: false }, emailExists: false })
  }

  removeMember(event: Event, index: number) {
    if (this.submitInProcess) return;
    event.preventDefault()
    this.members.splice(index, 1)
  }


  initializeForms(): void {
    this.profileForm = this.fb.group({
      email: [this.email, [Validators.required, Validators.email]],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      gender: ['', Validators.required],
      username: ['', Validators.required],
      profilePicture: ['https://cdn.jsdelivr.net/gh/alohe/avatars/png/vibrent_6.png'],
      password: ['', Validators.required],
      confirmPassword: ['', Validators.required],
    }, { validators: [this.passwordMatchValidator] });

    this.organizationForm = this.fb.group({
      companyName: ['', Validators.required],
      workspaceUrl: ['inncircles.ai/'],
      companySize: [''],
      contact: [null],
      address: [''],
      companyEmail: ['', [Validators.required, Validators.email]],
      domain: [this.domain, [Validators.required]]
    });

    for (let i = 0; i < 2; i++) {
      this.members.push({ email: '', role: '', touched: { email: false, role: false }, emailExists: false })
    }
  }

  onEmailInput(index: number) {
    if (this.members[index]) {
      this.members[index].touched = this.members[index].touched || { email: false, role: false };
      this.members[index].touched!.email = true;
      // Clear emailExists error when user starts typing
      this.members[index].emailExists = false;
    }
  }

  onRoleChange(index: number) {
    if (this.members[index]) {
      this.members[index].touched = this.members[index].touched || { email: false, role: false };
      this.members[index].touched!.role = true;
    }
  }

  onCompanyNameChange(event: any) {
    const companyName = event.target.value;
    const workspaceUrl = this.organizationForm.get('workspaceUrl')?.value;
    if (workspaceUrl === 'inncircles.ai/') {
      this.workspaceInputValue = slug(companyName, '-').toLocaleLowerCase();
    }
    this.organizationForm.get('workspaceUrl')?.setValue(this.workspacePrefix + companyName);
  }

  avatarValidator() {
    const group = this.profileForm;
    const gender = group.get('gender')?.value;
    const currentPicture = group.get('profilePicture')?.value;

    if (!gender || currentPicture && !currentPicture.startsWith('http')) {
      return true; // Skip if a custom picture is already set
    }

    group.get('profilePicture')?.setValue(
      gender === 'MALE'
        ? this.PROFILE_PICTURES().MALE
        : this.PROFILE_PICTURES().FEMALE
    );
    return true;
  }

  passwordMatchValidator(group: FormGroup) {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { mismatch: true };
  }

  onSubmitProfile(): void {
    if (!this.profileForm.valid) {
      this.profileForm.markAllAsTouched();
      return
    }
    this.name = this.profileForm.get('firstName')?.value
    //username check 
    this.authService.checkUsername(this.profileForm.get('username')?.value).subscribe(res => {
      if (res.data) {
        this.profileForm.controls['username'].setErrors({ 'alreadyExists': true });
        this.toastService.error('Username already exists');
        return;
      }
      else {
        this.nextStep();
      }
    })

  }


  onSubmitOrganization(): void {
    if (!this.isOrganizationFormValid) {
      this.organizationForm.markAllAsTouched();
      return
    }

    //domain check
    this.authService.checkDomain(this.organizationForm.get('domain')?.value).subscribe(res => {
      if (res.data) {
        this.organizationForm.controls['domain'].setErrors({ 'alreadyExists': true });
        this.toastService.error('Domain already exists');
        return;
      }
      else {
        this.onSubmit();
      }
    })

  }

  onSubmitTeam(): void {
    if (this.members.some(member => member.email === this.profileForm.get('email')?.value)) {
      this.toastService.error('You cant add yourself as a team member');
      return
    }

    const membersWithoutRole = this.members.filter(member =>
      member.email && member.email.trim() && (!member.role || !member.role.trim())
    );

    if (membersWithoutRole.length > 0) {
      this.toastService.error('Please assign a role to all team members');
      return;
    }

    const invalidEmails = this.members.filter(member =>
      member.email && member.email.trim() && !this.isValidEmail(member.email)
    );

    if (invalidEmails.length > 0) {
      return;
    }

    const validMembers = this.members.filter(member =>
      member.email && member.email.trim() && member.role && member.role.trim() && this.isValidEmail(member.email)
    );

    if (validMembers.length === 0) {
      this.toastService.error('Please add at least one team member with valid email and role');
      return;
    }

    // Check if emails already exist
    const emailsToCheck = validMembers.map(member => member.email.trim());
    this.authService.checkEmails(emailsToCheck).subscribe({
      next: (response) => {
        if (response.success && response.data && response.data.length > 0) {
          // Clear previous emailExists flags
          this.members.forEach(member => {
            member.emailExists = false;
          });

          // Mark emails that exist
          let hasExistingEmails = false;
          response.data.forEach((existingEmail: { email: string, exists: boolean, message: string }) => {
            const memberIndex = this.members.findIndex(m =>
              m.email && m.email.trim().toLowerCase() === existingEmail.email.toLowerCase()
            );
            if (memberIndex !== -1) {
              this.members[memberIndex].emailExists = true;
              hasExistingEmails = true;
            }
          });

          if (hasExistingEmails) {
            this.toastService.error('Some emails are already registered. Please check the error messages below.');
            return;
          }
        }

        // All checks passed, proceed
        this.toastService.success('Invitation will be sent on successful completion of setup.');
        this.toggleTeamInviteStep();
      },
      error: (err) => {
        this.toastService.error('Error checking emails. Please try again.');
      }
    });
  }

  onFileSelect(event: Event) {
    if (this.submitInProcess) return;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      if (!this.isValidImageType(file.type)) {
        this.toastService.error('Please select a valid image file (JPEG, PNG, GIF)');
        input.value = '';
        return;
      }
      if (file.size > this.maxImageSize) {
        this.toastService.error('File size must be less than 5MB');
        input.value = '';
        return;
      }

      this.imageFile = file
      this.profileForm.patchValue({
        profilePicture: URL.createObjectURL(file),
      });
    }
    input.value = '';
  }

  isValidImageType(type: string): boolean {
    return this.validImageTypes.includes(type);
  }

  isValidEmail(email: string): boolean {
    if (!email || !email.trim()) return true;
    return validateEmail(email.trim());
  }

  hasEmailError(email: string, index: number): boolean {
    const member = this.members[index];
    if (!member || !member.touched?.email) {
      return false;
    }
    if (!email || !email.trim()) {
      return false;
    }
    return !this.isValidEmail(email);
  }

  hasRoleError(email: string, role: string, index: number): boolean {
    const member = this.members[index];
    if (!member || !member.touched?.role) {
      return false;
    }
    if (!email || !email.trim()) {
      return false;
    }
    return !role || !role.trim();
  }

  hasDuplicateEmail(email: string, currentIndex: number): boolean {
    if (!email || !email.trim()) {
      return false;
    }

    const normalizedEmail = email.trim().toLowerCase();
    return this.members.some((member, index) =>
      index !== currentIndex &&
      member.email &&
      member.email.trim().toLowerCase() === normalizedEmail
    );
  }

  get areMembersValid(): boolean {
    const filledMembers = this.members.filter(member =>
      member.email && member.email.trim()
    );

    if (filledMembers.length === 0) {
      return false;
    }

    const hasDuplicates = filledMembers.some((member, index) =>
      this.hasDuplicateEmail(member.email, index)
    );

    if (hasDuplicates) {
      return false;
    }

    return filledMembers.every(member =>
      this.isValidEmail(member.email) &&
      member.role &&
      member.role.trim()
    );
  }

  onLoginMethodToggle(method: any, enabled: boolean) {
    if (this.submitInProcess) return;
    // Check if user is trying to disable a method
    if (!enabled) {
      // Count how many methods are currently enabled (excluding the current one being toggled)
      const enabledCount = this.loginMethods.filter(m => m.value !== method.value && m.enabled).length;

      // If no other methods are enabled, prevent disabling this one
      if (enabledCount === 0) {
        this.toastService.error('At least one login method must be enabled');
        // Revert the toggle back to enabled using setTimeout to ensure change detection
        setTimeout(() => {
          method.enabled = true;
        }, 0);
        return;
      }
    }
  }

  onSubmit(): void {
    if (this.submitInProcess) return;

    // Check validity BEFORE disabling (disabling can affect validity)
    const isProfileFormValid = this.profileForm.valid;
    const isOrgFormValid = this.isOrganizationFormValid;

    if (!isProfileFormValid || !isOrgFormValid) {
      this.toastService.error('Please fill all the required fields');
      this.profileForm.markAllAsTouched();
      this.organizationForm.markAllAsTouched();
      return;
    }

    this.submitInProcess = true;
    // Disable all forms when submitting
    if (this.profileForm) {
      this.profileForm.disable();
    }
    if (this.organizationForm) {
      this.organizationForm.disable();
    }
    if (isProfileFormValid && isOrgFormValid) {
      const formData = new FormData();
      formData.append('firstName', this.profileForm.get('firstName')?.value);
      formData.append('lastName', this.profileForm.get('lastName')?.value);
      formData.append('fullName', this.profileForm.get('firstName')?.value + ' ' + this.profileForm.get('lastName')?.value);
      formData.append('email', this.profileForm.get('email')?.value);
      formData.append('username', this.profileForm.get('username')?.value);
      formData.append('gender', this.profileForm.get('gender')?.value);
      const file = this.profileForm.get('profilePicture')?.value;
      if (file) {
        formData.append('profilePicture', this.imageFile);
      }
      formData.append('password', this.profileForm.get('password')?.value);

      // Get enabled login methods
      const enabledLoginMethods = this.loginMethods
        .filter(m => m.enabled)
        .map(m => m.value);

      // Combine organization form values with login methods
      const organizationData = {
        ...this.organizationForm.value,
        loginMethods: enabledLoginMethods
      };
      formData.append('organization', JSON.stringify(organizationData));

      if (this.currentUser) {
        formData.append('userId', this.currentUser.id);
      }

      const membersWithEmail = this.members.filter(member => member.email && member.email.trim());
      membersWithEmail.forEach(member => {
        member.role = member.role.toUpperCase()
      })
      formData.append('members', JSON.stringify(membersWithEmail));

      this.authService.signUp(formData).pipe(catchError((error) => {
        this.submitInProcess = false;
        // Re-enable forms on error
        if (this.profileForm) {
          this.profileForm.enable();
        }
        if (this.organizationForm) {
          this.organizationForm.enable();
        }
        return error;
      })).subscribe({
        next: (userLoggedIn) => {
          if (userLoggedIn) {
            this.submitInProcess = false;
            // Re-enable forms on success (though navigation happens)
            if (this.profileForm) {
              this.profileForm.enable();
            }
            if (this.organizationForm) {
              this.organizationForm.enable();
            }
            this.toastService.success('Account created successfully');
            this.router.navigate(['']);
          }
        },
        error: (err) => {
          this.submitInProcess = false;
          // Re-enable forms on error
          if (this.profileForm) {
            this.profileForm.enable();
          }
          if (this.organizationForm) {
            this.organizationForm.enable();
          }
          this.toastService.error('Error', ' Something went wrong! Please try again');
        }
      })
    } else {
      this.toastService.error('Please fill all the required fields');
      this.submitInProcess = false;
      if (this.profileForm) {
        this.profileForm.enable();
      }
      if (this.organizationForm) {
        this.organizationForm.enable();
      }
      return;
    }
  }

  removePicture(): void {
    const gender = this.profileForm.get('gender')?.value;
    this.profileForm.patchValue({
      profilePicture: gender === 'MALE' ? this.PROFILE_PICTURES().MALE : this.PROFILE_PICTURES().FEMALE
    });
  }

  toggleTeamInviteStep() {
    if (this.submitInProcess) return;
    this.teamInviteStep = !this.teamInviteStep;
  }

  cancelTeamInviteStep() {
    if (this.submitInProcess) return;
    this.members = [];
    for (let i = 0; i < 2; i++) {
      this.members.push({ email: '', role: '', touched: { email: false, role: false }, emailExists: false })
    }
    this.teamInviteStep = false;
  }

  onContinue() {
    if (this.organizationForm.invalid) {
    }
    else {
      this.organizationForm.markAllAsTouched
    }
  }

  getUploadPictureLabel(): string {
    return 'Update Picture'
  }

  onPhoneNumberChange(phoneNumber: { countryCode: string, contactNumber: string }) { }
}
